import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import { useAuth } from 'react-oidc-context';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import cloudApi from '../../../app/api/cloudApi';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@iconify/react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import DeviceSettingsDialog from './device-settings-dialog';
import { formatFaceScannerModel } from '../../../utils/format-face-scanner-model';

function normalizeFaceScannerUserId(userId) {
  if (userId === null || userId === undefined) return '';
  return String(userId);
}

/** Device user that must not be bulk-selected or deleted from the UI (built-in admin). */
function isFaceScannerAdministratorUser(user) {
  const id = normalizeFaceScannerUserId(user?.userId ?? user?.UserId);
  if (id === '1') return true;
  const name = (user?.name ?? user?.Name ?? '').trim().toLowerCase();
  return name === 'administrator';
}

export default function FaceScannerDevicesPanel({ site, onDevicesChanged }) {
  const siteId = site?.id;
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const account = useSelector((state) => state.app.account);
  
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [enrolledUsers, setEnrolledUsers] = useState({});
  const [loadingUsers, setLoadingUsers] = useState({});
  const [userDetailsDialog, setUserDetailsDialog] = useState({ open: false, users: [], deviceId: null });
  const [userDetailsSelectedUserIds, setUserDetailsSelectedUserIds] = useState([]);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    bulk: false,
    userId: null,
    userName: null,
    userIds: null,
    deviceId: null,
  });
  const [deletingUser, setDeletingUser] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [deviceAccessLevels, setDeviceAccessLevels] = useState({});
  const [loadingDeviceAccessLevels, setLoadingDeviceAccessLevels] = useState({});
  const [assignAccessDialog, setAssignAccessDialog] = useState({
    open: false,
    deviceId: null,
    userId: null,
    selectedGroupIds: [],
    loading: false,
    saving: false,
    levels: [],
  });
  const [userDetailsSortField, setUserDetailsSortField] = useState('userId');
  const [userDetailsSortAscending, setUserDetailsSortAscending] = useState(true);

  const sortedUserDetailsUsers = useMemo(() => {
    const users = userDetailsDialog.users;
    if (!users?.length) return [];
    const mult = userDetailsSortAscending ? 1 : -1;
    return [...users].sort((a, b) => {
      if (userDetailsSortField === 'name') {
        const na = (a.name || '').toLocaleLowerCase();
        const nb = (b.name || '').toLocaleLowerCase();
        return na.localeCompare(nb, undefined, { sensitivity: 'base' }) * mult;
      }
      const numA = Number(a.userId);
      const numB = Number(b.userId);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return (numA - numB) * mult;
      }
      return String(a.userId ?? '').localeCompare(String(b.userId ?? '')) * mult;
    });
  }, [userDetailsDialog.users, userDetailsSortField, userDetailsSortAscending]);

  const userDetailsSelectableSortedUsers = useMemo(
    () => sortedUserDetailsUsers.filter((u) => !isFaceScannerAdministratorUser(u)),
    [sortedUserDetailsUsers]
  );

  const userDetailsSelectAllState = useMemo(() => {
    const selectableIds = userDetailsSelectableSortedUsers.map((u) => normalizeFaceScannerUserId(u.userId));
    const selectedCount = selectableIds.filter((id) => userDetailsSelectedUserIds.includes(id)).length;
    return {
      checked: selectableIds.length > 0 && selectedCount === selectableIds.length,
      indeterminate: selectedCount > 0 && selectedCount < selectableIds.length,
    };
  }, [userDetailsSelectableSortedUsers, userDetailsSelectedUserIds]);

  const closeUserDetailsDialog = () => {
    setUserDetailsSortField('userId');
    setUserDetailsSortAscending(true);
    setUserDetailsSelectedUserIds([]);
    setUserDetailsDialog({ open: false, users: [], deviceId: null });
  };

  useEffect(() => {
    setError(null);
    setPageLoading(true);

    const initializePage = async () => {
      if (!auth.isAuthenticated || !auth.user) {
        setError("Please log in to access this page");
        setPageLoading(false);
        return;
      }

      if (!siteId) {
        setError("Site ID is required");
        setPageLoading(false);
        return;
      }

      try {
        await loadDevices();
      } catch (err) {
        console.error("Error loading page:", err);
        setError(err.response?.data?.message || err.message || "Failed to load page");
      } finally {
        setPageLoading(false);
      }
    };

    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, auth.isAuthenticated, auth.user]);

  const loadDevices = useCallback(async () => {
    if (!auth.user?.access_token || !account?.id) {
      console.log("Missing authentication or account data");
      return;
    }

    setLoading(true);
    try {
      console.log("Loading face scanner devices...");
      const response = await cloudApi.getFaceScannerConnectedDevices(
        auth.user.access_token,
        siteId,
        account.id
      );
      
      if (response.data) {
        let loadedDevices = response.data;
        setDevices(loadedDevices);
        onDevicesChanged?.(loadedDevices);
        console.log(`Loaded ${loadedDevices.length} face scanner devices`);

        if (loadedDevices.length === 0) {
          enqueueSnackbar("No face scanner devices found for this site", { variant: "info" });
        } else {
          // GetConnectedDevices is a live device scan and knows nothing about persisted/renamed
          // names, so merge those in separately rather than changing that shared endpoint.
          try {
            const deviceIds = loadedDevices.map((d) => d.deviceID).filter((id) => id !== undefined && id !== null);
            const namesResponse = await cloudApi.getFaceScannerDeviceNames(
              auth.user.access_token,
              siteId,
              account.id,
              deviceIds
            );
            const persistedNames = namesResponse.data || {};
            if (Object.keys(persistedNames).length > 0) {
              loadedDevices = loadedDevices.map((d) => {
                const persistedName = persistedNames[d.deviceID];
                return persistedName ? { ...d, name: persistedName } : d;
              });
              setDevices(loadedDevices);
              onDevicesChanged?.(loadedDevices);
            }
          } catch (namesErr) {
            console.error("Error loading persisted device names:", namesErr);
          }
        }
      } else {
        setDevices([]);
        onDevicesChanged?.([]);
        enqueueSnackbar("No face scanner devices found", { variant: "info" });
      }
    } catch (err) {
      console.error("Error loading devices:", err);
      const errorMessage = err.response?.data || err.message || "Failed to load face scanner devices";
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, account, siteId, enqueueSnackbar]);

  const loadEnrolledUsers = useCallback(async (deviceId) => {
    if (!auth.user?.access_token || !account?.id) {
      return;
    }

    setLoadingUsers(prev => ({ ...prev, [deviceId]: true }));
    try {
      console.log(`Loading enrolled users for device ${deviceId}...`);
      const response = await cloudApi.getFaceScannerEnrolledUsers(
        auth.user.access_token,
        siteId,
        account.id,
        deviceId
      );
      
      if (response.data) {
        setEnrolledUsers(prev => ({ ...prev, [deviceId]: response.data }));
        console.log(`Loaded ${response.data.length} enrolled users for device ${deviceId}`);
      } else {
        setEnrolledUsers(prev => ({ ...prev, [deviceId]: [] }));
      }
    } catch (err) {
      console.error(`Error loading users for device ${deviceId}:`, err);
      const errorMessage = err.response?.data || err.message || "Failed to load enrolled users";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoadingUsers(prev => ({ ...prev, [deviceId]: false }));
    }
  }, [auth.user, account, siteId, enqueueSnackbar]);

  const loadDeviceAccessLevels = useCallback(async (deviceId) => {
    if (!auth.user?.access_token || !account?.id) {
      return [];
    }

    setLoadingDeviceAccessLevels((prev) => ({ ...prev, [deviceId]: true }));
    let list = [];
    try {
      const response = await cloudApi.getFaceScannerDeviceAccessLevels(
        auth.user.access_token,
        siteId,
        account.id,
        deviceId
      );
      list = Array.isArray(response.data) ? response.data : [];
      setDeviceAccessLevels((prev) => ({
        ...prev,
        [deviceId]: list,
      }));
    } catch (err) {
      console.error(`Error loading access levels for device ${deviceId}:`, err);
      const errorMessage = err.response?.data || err.message || 'Failed to load access levels';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setDeviceAccessLevels((prev) => ({ ...prev, [deviceId]: [] }));
    } finally {
      setLoadingDeviceAccessLevels((prev) => ({ ...prev, [deviceId]: false }));
    }
    return list;
  }, [auth.user, account, siteId, enqueueSnackbar]);

  const handleDeviceExpand = (deviceId) => {
    if (expandedDevice === deviceId) {
      setExpandedDevice(null);
    } else {
      setExpandedDevice(deviceId);
      if (!enrolledUsers[deviceId]) {
        loadEnrolledUsers(deviceId);
      }
      if (!deviceAccessLevels[deviceId] && !loadingDeviceAccessLevels[deviceId]) {
        loadDeviceAccessLevels(deviceId);
      }
    }
  };

  const openAssignAccessDialog = async (deviceId, userId) => {
    if (!auth.user?.access_token || !account?.id) {
      return;
    }
    setAssignAccessDialog({
      open: true,
      deviceId,
      userId,
      selectedGroupIds: [],
      loading: true,
      saving: false,
      levels: [],
    });
    try {
      const levelsFromDevice = await loadDeviceAccessLevels(deviceId);
      const detailsRes = await cloudApi.getFaceScannerUserDetails(
        auth.user.access_token,
        siteId,
        account.id,
        deviceId,
        [userId]
      );
      const row = detailsRes.data?.[0];
      const ids = row?.accessGroupIds ?? row?.AccessGroupIds ?? [];
      setAssignAccessDialog((prev) => ({
        ...prev,
        open: true,
        deviceId,
        userId,
        levels: levelsFromDevice,
        selectedGroupIds: Array.isArray(ids) ? [...ids] : [],
        loading: false,
        saving: false,
      }));
    } catch (err) {
      console.error('Error loading user access groups:', err);
      enqueueSnackbar(err.response?.data || err.message || 'Failed to load current access groups', {
        variant: 'error',
      });
      setAssignAccessDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const toggleAssignGroupId = (groupId) => {
    setAssignAccessDialog((prev) => {
      const set = new Set(prev.selectedGroupIds);
      if (set.has(groupId)) {
        set.delete(groupId);
      } else {
        set.add(groupId);
      }
      return { ...prev, selectedGroupIds: [...set] };
    });
  };

  const handleSaveAssignAccessGroups = async () => {
    const { deviceId, userId, selectedGroupIds } = assignAccessDialog;
    if (!deviceId || !userId || !auth.user?.access_token || !account?.id) {
      return;
    }
    setAssignAccessDialog((prev) => ({ ...prev, saving: true }));
    try {
      await cloudApi.setFaceScannerUserAccessGroups(
        auth.user.access_token,
        siteId,
        account.id,
        deviceId,
        userId,
        selectedGroupIds
      );
      enqueueSnackbar('Access groups updated on device', { variant: 'success' });
      setAssignAccessDialog({
        open: false,
        deviceId: null,
        userId: null,
        selectedGroupIds: [],
        loading: false,
        saving: false,
        levels: [],
      });
      loadEnrolledUsers(deviceId);
      if (userDetailsDialog.open && userDetailsDialog.deviceId === deviceId) {
        const userIds = userDetailsDialog.users.map((u) => u.userId).filter(Boolean);
        if (userIds.length) {
          const response = await cloudApi.getFaceScannerUserDetails(
            auth.user.access_token,
            siteId,
            account.id,
            deviceId,
            userIds
          );
          if (response.data) {
            setUserDetailsDialog((d) => ({ ...d, users: response.data }));
          }
        }
      }
    } catch (err) {
      console.error('Error saving access groups:', err);
      enqueueSnackbar(err.response?.data || err.message || 'Failed to update access groups', {
        variant: 'error',
      });
      setAssignAccessDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleViewUserDetails = async (deviceId, users) => {
    setLoadingUserDetails(true);
    setUserDetailsSelectedUserIds([]);
    setUserDetailsDialog({ open: true, users: [], deviceId });

    try {
      const userIds = users.map(user => user.userId);
      console.log(`Loading user details for device ${deviceId}, users:`, userIds);
      
      const response = await cloudApi.getFaceScannerUserDetails(
        auth.user.access_token,
        siteId,
        account.id,
        deviceId,
        userIds
      );
      
      if (response.data) {
        setUserDetailsDialog({ open: true, users: response.data, deviceId });
        console.log(`Loaded details for ${response.data.length} users`);
      }
    } catch (err) {
      console.error("Error loading user details:", err);
      const errorMessage = err.response?.data || err.message || "Failed to load user details";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleDeleteUser = (userId, userName, deviceId) => {
    if (isFaceScannerAdministratorUser({ userId, name: userName })) {
      return;
    }
    setDeleteConfirmDialog({
      open: true,
      bulk: false,
      userId,
      userName: userName || userId,
      userIds: null,
      deviceId,
    });
  };

  const handleOpenBulkDeleteConfirm = () => {
    if (userDetailsSelectedUserIds.length === 0 || !userDetailsDialog.deviceId) return;
    setDeleteConfirmDialog({
      open: true,
      bulk: true,
      userId: null,
      userName: null,
      userIds: [...userDetailsSelectedUserIds],
      deviceId: userDetailsDialog.deviceId,
    });
  };

  const toggleUserDetailsRowSelected = (userId) => {
    const id = normalizeFaceScannerUserId(userId);
    setUserDetailsSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllUserDetails = () => {
    const selectableIds = userDetailsSelectableSortedUsers.map((u) => normalizeFaceScannerUserId(u.userId));
    const allSelected =
      selectableIds.length > 0 && selectableIds.every((id) => userDetailsSelectedUserIds.includes(id));
    setUserDetailsSelectedUserIds(allSelected ? [] : selectableIds);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmDialog.bulk) {
      const ids = deleteConfirmDialog.userIds;
      const deviceId = deleteConfirmDialog.deviceId;
      if (!ids?.length || !deviceId) return;

      setDeletingUser(true);
      const deletedIds = [];
      const failedIds = [];
      try {
        for (const uid of ids) {
          try {
            await cloudApi.deleteFaceScannerUser(
              auth.user.access_token,
              siteId,
              account.id,
              deviceId,
              uid
            );
            deletedIds.push(normalizeFaceScannerUserId(uid));
          } catch (err) {
            console.error("Error deleting user in bulk:", err);
            failedIds.push(uid);
          }
        }

        setDeleteConfirmDialog({
          open: false,
          bulk: false,
          userId: null,
          userName: null,
          userIds: null,
          deviceId: null,
        });

        if (deletedIds.length > 0) {
          enqueueSnackbar(
            `Removed ${deletedIds.length} user(s) from device ${deviceId}`,
            { variant: "success" }
          );
          setUserDetailsDialog((d) => ({
            ...d,
            users: d.users.filter(
              (u) => !deletedIds.includes(normalizeFaceScannerUserId(u.userId))
            ),
          }));
          setUserDetailsSelectedUserIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
          loadEnrolledUsers(deviceId);
        }
        if (failedIds.length > 0) {
          enqueueSnackbar(
            `Failed to delete ${failedIds.length} user(s) from the device`,
            { variant: "error" }
          );
        }
      } finally {
        setDeletingUser(false);
      }
      return;
    }

    if (!deleteConfirmDialog.userId || !deleteConfirmDialog.deviceId) {
      return;
    }

    const removedId = normalizeFaceScannerUserId(deleteConfirmDialog.userId);

    setDeletingUser(true);
    try {
      console.log(`Deleting user ${deleteConfirmDialog.userId} from device ${deleteConfirmDialog.deviceId}...`);

      await cloudApi.deleteFaceScannerUser(
        auth.user.access_token,
        siteId,
        account.id,
        deleteConfirmDialog.deviceId,
        deleteConfirmDialog.userId
      );

      enqueueSnackbar(`User ${deleteConfirmDialog.userName} successfully deleted from device`, { variant: "success" });

      setDeleteConfirmDialog({
        open: false,
        bulk: false,
        userId: null,
        userName: null,
        userIds: null,
        deviceId: null,
      });

      const remainingUsers = userDetailsDialog.users.filter(
        (user) => normalizeFaceScannerUserId(user.userId) !== removedId
      );
      setUserDetailsDialog({ ...userDetailsDialog, users: remainingUsers });
      setUserDetailsSelectedUserIds((prev) => prev.filter((id) => id !== removedId));

      loadEnrolledUsers(deleteConfirmDialog.deviceId);
    } catch (err) {
      console.error("Error deleting user:", err);
      const errorMessage = err.response?.data || err.message || "Failed to delete user";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setDeletingUser(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmDialog({
      open: false,
      bulk: false,
      userId: null,
      userName: null,
      userIds: null,
      deviceId: null,
    });
  };

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase() || '';
    let color = 'default';
    
    if (statusLower.includes('connected') || statusLower.includes('online')) {
      color = 'success';
    } else if (statusLower.includes('disconnected') || statusLower.includes('offline')) {
      color = 'error';
    } else if (statusLower.includes('connecting') || statusLower.includes('pending')) {
      color = 'warning';
    }
    
    return (
      <Chip 
        label={status} 
        color={color} 
        size="small" 
        variant="outlined"
      />
    );
  };

  const handleDeviceRenamed = (deviceId, newName) => {
    setDevices((prev) => {
      const updated = prev.map((d) => (d.deviceID === deviceId ? { ...d, name: newName } : d));
      onDevicesChanged?.(updated);
      return updated;
    });
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Devices</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Icon icon="solar:settings-bold" />}
            onClick={() => setSettingsDialogOpen(true)}
            disabled={!site}
          >
            Device Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<Icon icon="solar:refresh-bold" />}
            onClick={loadDevices}
            disabled={loading}
          >
            Refresh Devices
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
          {/* Devices Table */}
          <Grid xs={12}>
            <Card>
              <CardHeader 
                title="Connected Face Scanner Devices" 
                action={
                  loading && <CircularProgress size={24} />
                }
              />
              <CardContent>
                {devices.length === 0 && !loading ? (
                  <Alert severity="info">
                    No face scanner devices found for this site.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width="50px" />
                          <TableCell>Device ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Model</TableCell>
                          <TableCell>IP Address</TableCell>
                          <TableCell>Port</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>SSL</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {devices.map((device) => (
                          <React.Fragment key={device.deviceID}>
                            <TableRow hover>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeviceExpand(device.deviceID)}
                                >
                                  <Icon 
                                    icon={expandedDevice === device.deviceID ? "solar:alt-arrow-up-bold" : "solar:alt-arrow-down-bold"} 
                                  />
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {device.deviceID}
                                </Typography>
                              </TableCell>
                              <TableCell>{device.name || 'N/A'}</TableCell>
                              <TableCell>
                                {formatFaceScannerModel(device.modelName, device.type)}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {device.ipAddress || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>{device.port || 'N/A'}</TableCell>
                              <TableCell>{getStatusChip(device.status)}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={device.useSSL ? 'Yes' : 'No'} 
                                  color={device.useSSL ? 'success' : 'default'} 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View Enrolled Users">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeviceExpand(device.deviceID)}
                                    disabled={loadingUsers[device.deviceID]}
                                  >
                                    <Icon icon="solar:users-group-two-rounded-bold" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            
                            {/* Expanded row showing enrolled users */}
                            <TableRow>
                              <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                <Collapse in={expandedDevice === device.deviceID} timeout="auto" unmountOnExit>
                                  <Box sx={{ margin: 2 }}>
                                    <Typography variant="h6" gutterBottom component="div">
                                      Enrolled Users ({enrolledUsers[device.deviceID]?.length || 0})
                                    </Typography>
                                    {loadingUsers[device.deviceID] ? (
                                      <Box display="flex" justifyContent="center" p={2}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : enrolledUsers[device.deviceID]?.length > 0 ? (
                                      <>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>User ID</TableCell>
                                              <TableCell>Faces</TableCell>
                                              <TableCell>Fingers</TableCell>
                                              <TableCell>Cards</TableCell>
                                              <TableCell>Access Groups</TableCell>
                                              <TableCell align="right">Assign</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {enrolledUsers[device.deviceID].map((user) => (
                                              <TableRow key={user.userId}>
                                                <TableCell>
                                                  <Typography variant="body2" fontFamily="monospace">
                                                    {user.userId}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell>{user.numOfFace}</TableCell>
                                                <TableCell>{user.numOfFinger}</TableCell>
                                                <TableCell>{user.numOfCard}</TableCell>
                                                <TableCell>{user.numOfAccessGroup}</TableCell>
                                                <TableCell align="right">
                                                  <Tooltip title="Assign Suprema access levels">
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => openAssignAccessDialog(device.deviceID, user.userId)}
                                                    >
                                                      <Icon icon="solar:key-minimalistic-square-3-bold" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                        <Box mt={2}>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Icon icon="solar:eye-bold" />}
                                            onClick={() => handleViewUserDetails(device.deviceID, enrolledUsers[device.deviceID])}
                                            disabled={loadingUserDetails}
                                          >
                                            View Detailed User Information
                                          </Button>
                                        </Box>
                                      </>
                                    ) : (
                                      <Alert severity="info">
                                        No enrolled users found for this device.
                                      </Alert>
                                    )}
                                    <Divider sx={{ my: 3 }} />
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                      <Typography variant="h6" component="div">
                                        Access levels (Suprema) ({deviceAccessLevels[device.deviceID]?.length ?? 0})
                                      </Typography>
                                      <Tooltip title="Refresh access levels from device">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => loadDeviceAccessLevels(device.deviceID)}
                                            disabled={loadingDeviceAccessLevels[device.deviceID]}
                                          >
                                            {loadingDeviceAccessLevels[device.deviceID] ? (
                                              <CircularProgress size={20} />
                                            ) : (
                                              <Icon icon="solar:refresh-bold" />
                                            )}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                    {loadingDeviceAccessLevels[device.deviceID] &&
                                    (deviceAccessLevels[device.deviceID] === undefined ||
                                      deviceAccessLevels[device.deviceID]?.length === 0) ? (
                                      <Box display="flex" justifyContent="center" p={2}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : deviceAccessLevels[device.deviceID]?.length > 0 ? (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Name</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {deviceAccessLevels[device.deviceID].map((level) => {
                                            const lid = level.id ?? level.Id;
                                            const lname = level.name ?? level.Name ?? '';
                                            return (
                                              <TableRow key={lid}>
                                                <TableCell>
                                                  <Typography variant="body2" fontFamily="monospace">
                                                    {lid}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell>{lname || '—'}</TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    ) : (
                                      <Alert severity="info">
                                        No access levels returned for this device. Use Access Level Designer to push levels
                                        to Suprema, or refresh after configuring the device.
                                      </Alert>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog
          open={assignAccessDialog.open}
          onClose={() =>
            !assignAccessDialog.saving &&
            setAssignAccessDialog({
              open: false,
              deviceId: null,
              userId: null,
              selectedGroupIds: [],
              loading: false,
              saving: false,
              levels: [],
            })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Assign access levels (Suprema)</DialogTitle>
          <DialogContent>
            {assignAccessDialog.loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Device {assignAccessDialog.deviceId} — user{' '}
                  <Typography component="span" variant="body2" fontFamily="monospace">
                    {assignAccessDialog.userId}
                  </Typography>
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Selected groups replace this user&apos;s access group list on the device. Leave all unchecked to clear
                  groups (if the device allows it).
                </Alert>
                <FormControl component="fieldset" variant="standard" fullWidth>
                  <FormLabel component="legend">Access groups</FormLabel>
                  <FormGroup>
                    {(assignAccessDialog.levels || []).map((level) => {
                      const lid = level.id ?? level.Id;
                      const lname = level.name ?? level.Name ?? '';
                      return (
                        <FormControlLabel
                          key={lid}
                          control={
                            <Checkbox
                              checked={assignAccessDialog.selectedGroupIds.includes(lid)}
                              onChange={() => toggleAssignGroupId(lid)}
                            />
                          }
                          label={`${lid} — ${lname || 'Unnamed'}`}
                        />
                      );
                    })}
                  </FormGroup>
                </FormControl>
                {(assignAccessDialog.levels || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No access levels loaded. Expand the device row to refresh the list from the device.
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setAssignAccessDialog({
                  open: false,
                  deviceId: null,
                  userId: null,
                  selectedGroupIds: [],
                  loading: false,
                  saving: false,
                  levels: [],
                })
              }
              disabled={assignAccessDialog.saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAssignAccessGroups}
              disabled={assignAccessDialog.loading || assignAccessDialog.saving}
              startIcon={
                assignAccessDialog.saving ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              {assignAccessDialog.saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Details Dialog */}
        <Dialog
          open={userDetailsDialog.open}
          onClose={closeUserDetailsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            User Details - Device {userDetailsDialog.deviceId}
          </DialogTitle>
          <DialogContent>
            {loadingUserDetails ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {sortedUserDetailsUsers.length > 0 && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, pt: 1 }} alignItems={{ sm: 'center' }} flexWrap="wrap">
                    {userDetailsSelectableSortedUsers.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={userDetailsSelectAllState.checked}
                            indeterminate={userDetailsSelectAllState.indeterminate}
                            onChange={toggleSelectAllUserDetails}
                            inputProps={{ 'aria-label': 'Select all users except administrator' }}
                          />
                        }
                        label="Select all"
                      />
                    )}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="user-details-sort-field-label">Sort by</InputLabel>
                      <Select
                        labelId="user-details-sort-field-label"
                        label="Sort by"
                        value={userDetailsSortField}
                        onChange={(e) => setUserDetailsSortField(e.target.value)}
                      >
                        <MenuItem value="userId">User ID</MenuItem>
                        <MenuItem value="name">Name</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="user-details-sort-dir-label">Order</InputLabel>
                      <Select
                        labelId="user-details-sort-dir-label"
                        label="Order"
                        value={userDetailsSortAscending ? 'asc' : 'desc'}
                        onChange={(e) => setUserDetailsSortAscending(e.target.value === 'asc')}
                      >
                        <MenuItem value="asc">Ascending</MenuItem>
                        <MenuItem value="desc">Descending</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                )}
                <List>
                  {sortedUserDetailsUsers.map((user, index) => {
                    const isAdmin = isFaceScannerAdministratorUser(user);
                    const rowId = normalizeFaceScannerUserId(user.userId);
                    return (
                    <React.Fragment key={user.userId || index}>
                      <ListItem
                        alignItems="flex-start"
                        secondaryAction={
                          !isAdmin ? (
                          <Tooltip title="Delete user from this device">
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleDeleteUser(user.userId, user.name, userDetailsDialog.deviceId)}
                            >
                              <Icon icon="solar:trash-bin-trash-bold" />
                            </IconButton>
                          </Tooltip>
                          ) : null
                        }
                      >
                        {isAdmin ? (
                          <Box sx={{ width: 42, flexShrink: 0, mr: 1 }} aria-hidden />
                        ) : (
                          <Checkbox
                            edge="start"
                            tabIndex={-1}
                            disableRipple
                            checked={userDetailsSelectedUserIds.includes(rowId)}
                            onChange={() => toggleUserDetailsRowSelected(user.userId)}
                            inputProps={{ 'aria-labelledby': `user-details-label-${rowId}` }}
                            sx={{ py: 0.5, mr: 1, alignSelf: 'flex-start', mt: 0.5 }}
                          />
                        )}
                        <Avatar sx={{ mr: 2 }}>
                          {user.photo ? (
                            <img src={`data:image/jpeg;base64,${user.photo}`} alt="User" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Icon icon="solar:user-bold" />
                          )}
                        </Avatar>
                        <ListItemText
                          id={isAdmin ? undefined : `user-details-label-${rowId}`}
                          primary={user.name || user.userId || 'Unknown User'}
                          secondary={
                            <Box>
                              <Typography variant="body2" component="div">
                                User ID: {user.userId || 'N/A'}
                              </Typography>
                              <Typography variant="body2" component="div">
                                Faces: {user.numOfFace}, Fingers: {user.numOfFinger}, Cards: {user.numOfCard}
                              </Typography>
                              <Typography variant="body2" component="div">
                                Access Groups: {user.numOfAccessGroup}
                                {(user.accessGroupIds?.length > 0 || user.AccessGroupIds?.length > 0) && (
                                  <Typography component="span" variant="body2" display="block" sx={{ mt: 0.5 }}>
                                    Group IDs:{' '}
                                    {(user.accessGroupIds || user.AccessGroupIds || []).join(', ')}
                                  </Typography>
                                )}
                              </Typography>
                              {user.faces && user.faces.length > 0 && (
                                <Typography variant="body2" component="div">
                                  Face Data: {user.faces.length} face templates
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < sortedUserDetailsUsers.length - 1 && <Divider />}
                    </React.Fragment>
                    );
                  })}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
            <Button
              color="error"
              variant="outlined"
              disabled={userDetailsSelectedUserIds.length === 0 || loadingUserDetails}
              onClick={handleOpenBulkDeleteConfirm}
              startIcon={<Icon icon="solar:trash-bin-trash-bold" />}
            >
              Delete selected ({userDetailsSelectedUserIds.length})
            </Button>
            <Button onClick={closeUserDetailsDialog}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmDialog.open}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Icon icon="solar:danger-triangle-bold" width={24} style={{ color: '#f44336' }} />
              Confirm Deletion
            </Box>
          </DialogTitle>
          <DialogContent>
            {deleteConfirmDialog.bulk ? (
              <>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to delete{' '}
                  <strong>{deleteConfirmDialog.userIds?.length ?? 0}</strong> selected user(s) from device{' '}
                  <strong>{deleteConfirmDialog.deviceId}</strong>?
                </Typography>
                <Box
                  component="ul"
                  sx={{ mt: 2, pl: 2.5, maxHeight: 220, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1, py: 1 }}
                >
                  {(deleteConfirmDialog.userIds || []).map((uid) => {
                    const u = userDetailsDialog.users.find(
                      (row) => normalizeFaceScannerUserId(row.userId) === normalizeFaceScannerUserId(uid)
                    );
                    const label = u?.name || uid;
                    return (
                      <Typography key={uid} component="li" variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                        <strong>{label}</strong> — ID {uid}
                      </Typography>
                    );
                  })}
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to delete the following user from device <strong>{deleteConfirmDialog.deviceId}</strong>?
                </Typography>
                <Box mt={2} p={2} bgcolor="action.hover" borderRadius={1}>
                  <Typography variant="body2" color="text.secondary">
                    User: <strong>{deleteConfirmDialog.userName}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    User ID: <strong>{deleteConfirmDialog.userId}</strong>
                  </Typography>
                </Box>
              </>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will remove the user from this specific device only. The user will remain in the system and on other devices.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} disabled={deletingUser}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete} 
              color="error" 
              variant="contained"
              disabled={deletingUser}
              startIcon={deletingUser ? <CircularProgress size={20} /> : <Icon icon="solar:trash-bin-trash-bold" />}
            >
              {deletingUser ? 'Deleting...' : deleteConfirmDialog.bulk ? 'Delete all' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

      {/* Device Settings Dialog */}
      <DeviceSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        site={site}
        onDeviceRenamed={handleDeviceRenamed}
      />
    </>
  );
}